<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { api } from '../js/api.js';
    import { auth } from '../js/auth.js';
    import { toast } from '../js/toast.js';

    export let isOpen = false;
    export let userId = '';

    const dispatch = createEventDispatcher();

    let step = 1; // 1 = enter new email, 2 = enter verification code
    let newEmail = '';
    let verificationCode = '';
    let verificationUuid = '';
    let errors: { email?: string; code?: string } = {};
    let isLoading = false;

    const close = () => {
        isOpen = false;
        step = 1;
        newEmail = '';
        verificationCode = '';
        verificationUuid = '';
        errors = {};
        dispatch('close');
    };

    const requestEmailChange = async () => {
        errors = {};
        
        if (!newEmail || !newEmail.includes('@')) {
            errors.email = 'Please enter a valid email address';
            return;
        }

        isLoading = true;
        const response = await api.requestEmailChange(userId, newEmail);
        isLoading = false;

        if (response.success) {
            verificationUuid = response.data.verification_uuid;
            step = 2;
            toast.showNotification(response.success, response.message || 'Verification code sent to your new email.');
        } else {
            errors.email = response.message || 'Failed to send verification code.';
            toast.showNotification(response.success, response.message || 'Failed to send verification code.');
        }
    };

    const verifyEmailChange = async () => {
        errors = {};
        
        if (!verificationCode || verificationCode.length !== 6) {
            errors.code = 'Please enter the 6-digit verification code';
            return;
        }

        isLoading = true;
        const response = await api.verifyEmailChange(userId, verificationCode, verificationUuid);
        isLoading = false;

        if (response.success) {
            toast.showNotification(response.success, response.message || 'Email address updated successfully.');
            // Refresh auth data
            await auth.userRefresh();
            close();
            dispatch('emailChanged');
        } else {
            errors.code = response.message || 'Invalid verification code';
            toast.showNotification(response.success, response.message || 'Invalid verification code');
        }
    };

    const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (step === 1) {
                requestEmailChange();
            } else {
                verifyEmailChange();
            }
        }
    };
</script>

<svelte:window on:keydown={(e) => isOpen && e.key === 'Escape' && close()} />

{#if isOpen}
<div class="modal-overlay" role="dialog" aria-modal="true">
    <button type="button" class="modal-backdrop" aria-label="Close dialog" on:click={close}></button>
    <div class="modal-content" role="document">
        <div class="modal-header">
            <h3>Change Email Address</h3>
            <button class="close-button" on:click={close} aria-label="Close">&times;</button>
        </div>

        {#if step === 1}
        <div class="modal-body">
            <p style="margin-bottom: 16px; color: var(--grey-secondary);">
                Enter your new email address. We'll send you a verification code to confirm the change.
            </p>
            
            <div class="field">
                <label for="new-email">New Email Address</label>
                <input 
                    id="new-email"
                    type="email" 
                    bind:value={newEmail}
                    placeholder="your.new.email@example.com"
                    on:keypress={handleKeyPress}
                    disabled={isLoading}
                />
                {#if errors.email}
                    <span class="error">{errors.email}</span>
                {/if}
            </div>
        </div>

        <div class="modal-footer">
            <button type="button" on:click={close} class="ui button" disabled={isLoading}>Cancel</button>
            <button type="button" on:click={requestEmailChange} class="ui button primary blue" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Verification Code'}
            </button>
        </div>
        {:else}
        <div class="modal-body">
            <p style="margin-bottom: 16px; color: var(--grey-secondary);">
                A 6-digit verification code has been sent to <strong>{newEmail}</strong>. Please enter the code below:
            </p>
            
            <div class="field">
                <label for="verification-code">Verification Code</label>
                <input 
                    id="verification-code"
                    type="text" 
                    bind:value={verificationCode}
                    placeholder="000000"
                    maxlength="6"
                    on:keypress={handleKeyPress}
                    on:input={() => {
                        verificationCode = verificationCode.replace(/\D/g, '').slice(0, 6);
                        if (errors.code) {
                            delete errors.code;
                        }
                    }}
                    disabled={isLoading}
                    style="width: 200px; font-size: 24px; text-align: center; letter-spacing: 8px; font-weight: 500;"
                />
                {#if errors.code}
                    <span class="error">{errors.code}</span>
                {/if}
            </div>

            <div style="margin-top: 16px;">
                <button type="button" on:click={() => { step = 1; verificationCode = ''; errors = {}; }} class="text-button">
                    ← Change email address
                </button>
            </div>
        </div>

        <div class="modal-footer">
            <button type="button" on:click={close} class="ui button" disabled={isLoading}>Cancel</button>
            <button type="button" on:click={verifyEmailChange} class="ui button primary blue" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify & Update Email'}
            </button>
        </div>
        {/if}
    </div>
</div>
{/if}

<style>
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 2000;
        animation: fadeIn 0.3s ease;
    }

    .modal-backdrop {
        position: absolute;
        inset: 0;
        border: none;
        padding: 0;
        margin: 0;
        background-color: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        cursor: default;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    @keyframes slideUp {
        from { 
            opacity: 0;
            transform: translateY(20px);
        }
        to { 
            opacity: 1;
            transform: translateY(0);
        }
    }

    .modal-content {
        position: relative;
        z-index: 1;
        background-color: var(--white);
        border-radius: 16px;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
        max-width: 520px;
        width: 90%;
        max-height: 90vh;
        overflow-y: auto;
        animation: slideUp 0.3s ease;
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 24px 28px;
        border-bottom: 2px solid var(--greyish);
        background: linear-gradient(135deg, rgba(5, 108, 242, 0.03) 0%, rgba(255, 154, 86, 0.03) 100%);
    }

    .modal-header h3 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 500;
        color: var(--grey);
    }

    .close-button {
        background: var(--greyish);
        border: none;
        font-size: 24px;
        line-height: 1;
        color: var(--grey-secondary);
        cursor: pointer;
        padding: 0;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        border-radius: 8px;
    }

    .close-button:hover {
        background: var(--grey-secondary);
        color: var(--white);
        transform: rotate(90deg);
    }

    .modal-body {
        padding: 28px;
    }

    .field {
        margin-bottom: 20px;
    }

    .field label {
        display: block;
        margin-bottom: 12px;
        font-weight: 500;
        font-size: 1rem;
        color: var(--grey);
    }

    .field input {
        width: 100%;
        padding: 1rem 1.25rem;
        border: 2px solid var(--border-color);
        border-radius: 10px;
        font-size: 1.0625rem;
        transition: all 0.3s ease;
        background: var(--white);
    }

    .field input:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 4px rgba(5, 108, 242, 0.1);
        background: rgba(5, 108, 242, 0.02);
    }

    .field input:hover:not(:disabled) {
        border-color: var(--secondary);
    }

    .field input:disabled {
        background-color: var(--greyish);
        cursor: not-allowed;
        opacity: 0.7;
    }

    .error {
        display: block;
        color: #d91b42;
        font-size: 0.875rem;
        margin-top: 10px;
        font-weight: 500;
    }

    .text-button {
        background: none;
        border: none;
        color: var(--primary);
        cursor: pointer;
        padding: 8px 0;
        font-size: 0.9375rem;
        text-decoration: none;
        transition: all 0.3s ease;
        font-weight: 500;
    }

    .text-button:hover {
        color: var(--darkprimary);
        text-decoration: underline;
        transform: translateX(-4px);
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 24px 28px;
        border-top: 2px solid var(--greyish);
        background: var(--greyish);
    }

    /* Playful button styling */
    .modal-footer .ui.button {
        padding: 0.875rem 1.75rem;
        font-size: 1.0625rem;
        font-weight: 500;
        border-radius: 10px;
        transition: all 0.3s ease;
        border: 0;
        min-width: 120px;
    }

    /* Primary buttons - Orange gradient */
    .modal-footer .ui.button.primary.blue {
        background: linear-gradient(135deg, #ff9a56 0%, #ff7b39 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(255, 123, 57, 0.3);
    }

    .modal-footer .ui.button.primary.blue:hover:not(:disabled) {
        background: linear-gradient(135deg, #ff7b39 0%, #ff5e1a 100%);
        box-shadow: 0 6px 16px rgba(255, 123, 57, 0.4);
        transform: translateY(-2px);
    }

    .modal-footer .ui.button.primary.blue:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    /* Cancel button */
    .modal-footer .ui.button:not(.primary) {
        background: var(--white);
        color: var(--grey);
        border: 2px solid var(--border-color);
    }

    .modal-footer .ui.button:not(.primary):hover:not(:disabled) {
        background: var(--greyish);
        border-color: var(--grey-secondary);
        transform: translateY(-1px);
    }

    @media (max-width: 768px) {
        .modal-content {
            width: 95%;
            border-radius: 12px;
        }

        .modal-header,
        .modal-body,
        .modal-footer {
            padding: 20px;
        }

        .modal-header h3 {
            font-size: 1.25rem;
        }

        .field input {
            padding: 0.875rem 1rem;
            font-size: 1rem;
        }

        .modal-footer {
            flex-direction: column;
        }

        .modal-footer .ui.button {
            width: 100%;
        }
    }
</style>

